/**
 * Орки.
 *
 * The first thing in this world that comes after you. Everything before it
 * either stood still (the villagers) or flew in a straight line (the arrows);
 * this walks, climbs, and hits back.
 *
 * The whole of it is pure: positions, a mover, a clock and a hit test. It runs
 * headlessly, so "does a wall two blocks high actually stop them" is a
 * question the test suite can answer rather than something to find out by
 * being overrun.
 *
 * Deliberately stupid. There is no path-finding: an orc walks at you and jumps
 * when something is in the way. That is enough to make defences mean
 * something - a wall it cannot climb is a wall - and a clever enemy is a much
 * later problem than a working one.
 */

import { moveAABB, type SolidTest } from "../physics/voxelCollision.js";
import { Vitals } from "./vitals.js";
import { PlainItemId } from "../block/items.js";
import { BlockId } from "../block/registry.js";

export const ORC_MAX_HP = 24;
export const ORC_WIDTH = 0.7;
export const ORC_HEIGHT = 1.9;

/** Blocks a second. Slower than a running cossack, so you can outrun one. */
export const ORC_SPEED = 3.4;
/** How far it notices you. Through walls: it should come and beat on them. */
export const ORC_SIGHT = 26;
/** How close it has to be to swing. */
export const ORC_REACH = 1.9;
export const ORC_DAMAGE = 3;
/** Seconds between blows. */
export const ORC_SWING = 1.1;

/**
 * How high it steps without jumping, and how high it jumps.
 *
 * Deliberately *lower* than the player's step of 1.05: an orc that walks over
 * a block the way a cossack does would make every wall pointless. It steps
 * over a slab and has to jump a block, and a jump clears exactly one - so a
 * wall two high stops it, which is the rule the whole idea of defences hangs
 * on and which the tests state outright.
 */
export const ORC_STEP = 0.6;
/**
 * How hard two of them push each other apart, in blocks a second.
 *
 * Nothing kept one орк out of another's cell: they all walk at the same
 * speed towards the same person, so a band that sets off together arrives
 * together and stands in one place - "злі моби коли доганяють можуть
 * зливатись в одного". Four of them in one body look like one orc and fight
 * like four, which is the worst of both.
 *
 * A shove rather than a collision. Two blocks a second at full overlap,
 * against a walking speed of about three: enough to spread a band out around
 * whoever they are after within a second, not enough to stop them closing.
 * It is added to what the orc wanted to do and goes through the same
 * `moveAABB` as everything else, so being shoved never pushes one into a
 * wall - it is the walls that win.
 */
export const ORC_SHOVE = 2;
export const ORC_JUMP = 8.4;
const GRAVITY = 32;
const TERMINAL = 48;
/** How quickly it stops when it has nothing to walk at. */
const DAMPING = 8;

/**
 * The three kinds of orc.
 *
 * They were all one thing that walked at you and swung. Splitting them is
 * what makes a raid something to think about rather than something to back
 * away from: an archer punishes standing still in the open, a sabre orc
 * punishes standing still anywhere, and the leader arrives faster than you
 * expect and is still coming when the rest are down.
 */
/**
 * What walks at you and hits you.
 *
 * The name is historical: this file was written for the орки of the кремль
 * and it is now what every hostile thing in the game runs on, because
 * "walk towards the nearest person and swing when you are close enough" is
 * the same behaviour whether the thing doing it is an orc, a вовкулака or a
 * мавка. What differs between them is a handful of numbers and a skin.
 *
 * The raid names the kinds it sends by hand, so nothing added here ever
 * turns up in a кремль by accident.
 */
export type OrcKind =
  | "sabre" | "archer" | "leader"
  /** The bots' sapper: an orc with a кайло who cuts the way for the rest. */
  | "sapper"
  /** Вовкулака: the thing in the woods. */
  | "vovkulaka"
  /** Мавка: the thing in the lake, after dark. */
  | "mavka"
  /** The same мавка after dark: red-eyed, thorned, and coming for you. */
  | "mavkaNight"
  /** Скелет-страж: what stands in the rooms of the замок, two to a room. */
  | "skeleton"
  /** Чахлик Невмирущий himself. One of him, in the whole world. */
  | "chakhlyk";

export interface OrcBreed {
  readonly kind: OrcKind;
  readonly label: string;
  readonly en: string;
  readonly maxHp: number;
  /** Blocks a second. */
  readonly speed: number;
  readonly damage: number;
  /** Seconds between blows, or between arrows. */
  readonly swing: number;
  /**
   * How far off it stops and shoots, if it shoots at all.
   *
   * An archer that walked into sabre range would be a sabre orc with a bow -
   * so it closes to here and no further, and backs off if you close on it.
   */
  readonly shootFrom?: number;
  /** Which skin the renderer draws it with. */
  readonly skinId: string;
  /**
   * Cuts through what stands in its way, a block every this many seconds.
   * "Боти не можуть пробити стіну самі" - the sapper can, and the rest of
   * the army walks through the hole it leaves.
   */
  readonly digs?: number;
  /**
   * Keeps company and never strikes: the day мавка stands at the water and
   * turns to watch whoever comes, and that is all she does.
   */
  readonly friendly?: boolean;
  /**
   * Throws fire rather than arrows, and how far off it starts.
   *
   * Its own field beside `shootFrom` because the two are not the same act: an
   * archer looses a shaft that flies flat and sticks, and this lobs a ball of
   * cold fire that bursts where it lands. The stepper has to know which it is
   * before it can decide what to hand back.
   */
  readonly hurlFrom?: number;
  /**
   * What it carries in its fist, drawn in its hand.
   *
   * Its own field rather than being read off `drops`: what a thing is holding
   * and what it leaves behind are different questions, and an archer that
   * dropped five arrows would otherwise be drawn clutching a handful of them.
   * Left out for anything that fights bare-handed - a вовкулака has claws.
   */
  readonly wields?: number;
  /**
   * And what it carries on the other arm.
   *
   * Separate from `wields` because the two are drawn differently and by
   * different code: a blade points where the arm points, a shield is
   * strapped flat across the forearm. Left out for everything that fights
   * with both hands or with neither.
   */
  readonly shields?: number;
  /**
   * What it leaves on the ground.
   *
   * Every orc carries gold - which is where the metal for a dome comes from,
   * and the reason a кремль can be gilded at all. The унітаз is rare and has
   * no explanation; it is the joke, and explaining it would spoil it.
   */
  readonly drops: readonly {
    readonly item: number; readonly min: number; readonly max: number;
    /** 0-1. Certain when left out. */
    readonly chance?: number;
  }[];
}

export const ORC_SABRE: OrcBreed = {
  kind: "sabre", label: "орк із шаблею", en: "sabre orc",
  maxHp: ORC_MAX_HP, speed: ORC_SPEED,
  damage: ORC_DAMAGE, swing: ORC_SWING,
  skinId: "orc",
  wields: PlainItemId.WoodenSabre,
  drops: [
    { item: PlainItemId.GoldBit, min: 1, max: 2 },
    { item: BlockId.Toilet, min: 1, max: 1, chance: 0.04 },
  ],
};

/**
 * The sapper. Two walk with every bot army on «Сучасна війна»: when the way
 * to the enemy is shut - the wall turned to planks, whatever the players
 * have built - it cuts a way two blocks high, a block at a time, with an
 * iron кайло and so by the rules a player's iron кайло keeps: nothing
 * незламне, and no прапор.
 */
export const ORC_SAPPER: OrcBreed = {
  kind: "sapper", label: "орк-сапер", en: "orc sapper",
  maxHp: 20, speed: ORC_SPEED,
  damage: ORC_DAMAGE, swing: ORC_SWING,
  skinId: "orc",
  wields: PlainItemId.IronPickaxe,
  digs: 0.8,
  drops: [{ item: PlainItemId.GoldBit, min: 1, max: 2 }],
};

export const ORC_ARCHER: OrcBreed = {
  kind: "archer", label: "орк-лучник", en: "orc archer",
  // Thinner than the one with the sabre: it stands off and shoots, so it has
  // to be worth closing on rather than worth ignoring.
  maxHp: 16, speed: ORC_SPEED,
  damage: 0, swing: 2.2,
  shootFrom: 14,
  skinId: "orc_archer",
  wields: PlainItemId.Bow,
  drops: [
    { item: PlainItemId.GoldBit, min: 1, max: 2 },
    { item: PlainItemId.Arrow, min: 2, max: 5 },
    { item: BlockId.Toilet, min: 1, max: 1, chance: 0.04 },
  ],
};

/**
 * Орк-ватажок.
 *
 * Thirty per cent faster than the rest, which is the whole of its threat: you
 * cannot walk away from it the way you can from the others, and it arrives
 * while you are still dealing with the wave it came in with.
 */
export const ORC_LEADER: OrcBreed = {
  kind: "leader", label: "орк-ватажок", en: "orc leader",
  maxHp: 48, speed: ORC_SPEED * 1.3,
  damage: 6, swing: 0.9,
  skinId: "orc_leader",
  wields: PlainItemId.LeaderSabre,
  drops: [
    { item: PlainItemId.GoldBit, min: 4, max: 8 },
    // Its own sabre, always. It is the only way anybody gets one.
    { item: PlainItemId.LeaderSabre, min: 1, max: 1 },
    { item: BlockId.Toilet, min: 1, max: 1, chance: 0.25 },
  ],
};

/**
 * Вовкулака.
 *
 * Faster than an орк and hits harder, with less сила to take: it is meant to
 * be met in ones and twos in a wood, not in a wave, so it can afford to be
 * dangerous. Running is a real answer to it and only just - it is fifteen per
 * cent faster than a walk and slower than a sprint.
 *
 * It leaves its hide, and only its hide.
 *
 * Nothing for a long while - "you do not hunt a вовкулака, you get past one"
 * - which is still true of meeting one in the dark and was no use at all to
 * anybody who had killed one anyway. The skin burns like лляне полотно and
 * makes the same смолоскип: the difference between a night spent hiding and
 * a night spent lighting a cave.
 */
export const VOVKULAKA: OrcBreed = {
  kind: "vovkulaka", label: "вовкулака", en: "werewolf",
  maxHp: 20, speed: ORC_SPEED * 1.15,
  damage: 5, swing: 1.0,
  skinId: "vovkulaka",
  drops: [{ item: PlainItemId.VovkulakaHide, min: 1, max: 1 }],
};

/**
 * Мавка.
 *
 * Quicker still and lighter, and she only exists between dusk and dawn - the
 * sun is what deals with her, not a sabre. Less damage than the вовкулака
 * because what makes her a problem is where she is: you meet one at the water
 * in the dark, which is where you went to fill a bucket.
 */
export const MAVKA: OrcBreed = {
  kind: "mavka", label: "мавка", en: "mavka",
  maxHp: 14, speed: ORC_SPEED * 1.25,
  damage: 0, swing: 1.2,
  skinId: "mavka",
  friendly: true,
  drops: [],
};

/**
 * The same мавка after dark - "щоб мавка була дружньою вдень та атакувала
 * вночі". One creature in two skins: the green girl of the day goes at
 * dusk and this one rises in her place, red-eyed and thorned, and comes
 * for you; at dawn it is the other way round. Two breeds rather than one
 * that changes, because a body on the screen is made once for a kind and
 * a kind that changed under it would keep its old skin.
 */
export const MAVKA_NIGHT: OrcBreed = {
  kind: "mavkaNight", label: "нічна мавка", en: "night mavka",
  maxHp: 14, speed: ORC_SPEED * 1.25,
  damage: 4, swing: 1.2,
  skinId: "mavkaNight",
  drops: [],
};

/**
 * Скелет-страж: what walks the rooms of the замок.
 *
 * A helmet with a nose-guard, a red tabard over the ribs, a round shield and
 * an iron sword - the Kyivan-Rus kit off the reference, and deliberately not
 * the white stick-skeleton every other game draws. It is armoured, which is
 * what the сила is for: twenty-four points is more than a вовкулака, and the
 * sword hits harder than a sabre orc's.
 *
 * It does not wander and it does not raid. Two of them stand in each room of
 * the замок and they are put there when the замок is built - which is why
 * they are not in WILD_FOES and never will be.
 */
export const SKELETON: OrcBreed = {
  kind: "skeleton", label: "скелет-страж", en: "skeleton guard",
  maxHp: 24, speed: ORC_SPEED * 0.9,
  damage: 6, swing: 1.05,
  skinId: "skeleton",
  // An iron sword off a dead guard is the point of fighting one.
  wields: PlainItemId.IronSabre,
  shields: PlainItemId.WoodenShield,
  drops: [
    { item: PlainItemId.IronSabre, min: 1, max: 1, chance: 0.5 },
    { item: PlainItemId.WoodenShield, min: 1, max: 1, chance: 0.35 },
    { item: PlainItemId.Bone, min: 1, max: 3 },
  ],
};

/**
 * Чахлик Невмирущий.
 *
 * One of him in a world, sitting in a замок cut into a mountain, and he
 * throws cold blue fire. Everything about the numbers says boss: four times
 * the сила of a вовкулака, and he hurls from twenty blocks off rather than
 * closing - you cannot simply walk up to him and swing, which is what makes
 * the room he is in a fight rather than a scuffle.
 *
 * Slow on his feet on purpose. He is a dead thing in a crown, not a wolf: the
 * danger is the fire, and a boss that both outruns you and outranges you is
 * a boss nobody gets to learn.
 */
export const CHAKHLYK: OrcBreed = {
  kind: "chakhlyk", label: "Чахлик Невмирущий", en: "Chakhlyk the Undying",
  maxHp: 80, speed: ORC_SPEED * 0.75,
  damage: 8, swing: 2.2,
  hurlFrom: 20,
  skinId: "chakhlyk",
  drops: [
    { item: PlainItemId.GoldBit, min: 6, max: 12 },
    { item: PlainItemId.IronIngot, min: 3, max: 5 },
  ],
};

export const ORC_BREEDS: Readonly<Record<OrcKind, OrcBreed>> = {
  sapper: ORC_SAPPER,
  sabre: ORC_SABRE, archer: ORC_ARCHER, leader: ORC_LEADER,
  vovkulaka: VOVKULAKA, mavka: MAVKA, mavkaNight: MAVKA_NIGHT,
  skeleton: SKELETON, chakhlyk: CHAKHLYK,
};

/**
 * The ones that belong to the adventure rather than to a кремль.
 *
 * Kept as a list because three separate things need to agree about it: what
 * gets put down in the country, what the sun takes away at dawn, and what a
 * raid must never send.
 */
export const WILD_FOES: readonly OrcKind[] = ["vovkulaka", "mavka", "mavkaNight"];

/**
 * The three a кремль sends, and the two that belong to the замок.
 *
 * Written down rather than worked out. There used to be two kinds of foe -
 * the кремль's and the country's - so "not wild" meant "the кремль's" and a
 * check counted three that way. Then the замок arrived with guards of its
 * own, which are neither: they do not raid, they do not wander, and they
 * stand where they were put. Naming the sets is the difference between a
 * third category being *added* and a third category quietly joining whatever
 * a subtraction happened to leave behind.
 */
export const RAID_FOES: readonly OrcKind[] = ["sabre", "archer", "leader", "sapper"];
export const CASTLE_FOES: readonly OrcKind[] = ["skeleton", "chakhlyk"];

/** Does this one melt away when the sun comes up? */
export function nightOnly(kind: OrcKind): boolean {
  return kind === "mavkaNight";
}

/** Out by day and gone at dusk: the other half of the мавка. */
export function dayOnly(kind: OrcKind): boolean {
  return kind === "mavka";
}

export interface Orc {
  readonly id: number;
  readonly breed: OrcBreed;
  /**
   * The lair that sent it, if one did.
   *
   * Kept so a lair can be asked how many of its own are still alive - which
   * is what caps it at five and what makes breaking that lair worth doing.
   * Raid orcs come off the boats and belong to nobody.
   */
  readonly from?: { x: number; y: number; z: number };
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  yaw: number;
  onGround: boolean;
  readonly vitals: Vitals;
  /** Seconds until it can swing again. */
  swing: number;
  /** Seconds it has been cutting at what is in its way. Sappers only. */
  digging?: number;
  /**
   * How many blows it has struck, ever.
   *
   * A count rather than a flag, the way a player's blows travel: whoever
   * draws it keeps its own clock and starts a swing whenever the number goes
   * up, so a blow shows the same length whether the frame was early or late.
   * "Орки нехай рухають рукою зі зброєю, коли б'ють."
   */
  struck: number;
}

let nextId = 1;

export function makeOrc(
  at: { x: number; y: number; z: number },
  kind: OrcKind = "sabre",
  from?: { x: number; y: number; z: number },
): Orc {
  const breed = ORC_BREEDS[kind];
  return {
    id: nextId++,
    breed,
    ...(from !== undefined ? { from } : {}),
    x: at.x, y: at.y, z: at.z,
    vx: 0, vy: 0, vz: 0,
    yaw: 0,
    onGround: false,
    vitals: new Vitals(breed.maxHp),
    swing: 0,
    struck: 0,
  };
}

/**
 * An orc the server owns, drawn here.
 *
 * Made with the server's id rather than one of our own, because that id is
 * how a blow names which orc it landed on - two clients each counting from
 * one would send their sabres at each other's orcs. It has vitals like any
 * other so that it can flash when it is hit, but nothing here decides
 * anything: everything about it arrives on the wire.
 */
export function mirrorOrc(
  id: number,
  at: { x: number; y: number; z: number },
  kind: OrcKind,
): Orc {
  // Built rather than made-and-renumbered: `makeOrc` takes the next local id
  // as it goes, so mirroring a raid of thirty would push this world's own
  // numbering thirty along for orcs it never made.
  const breed = ORC_BREEDS[kind];
  return {
    id,
    breed,
    x: at.x, y: at.y, z: at.z,
    vx: 0, vy: 0, vz: 0,
    yaw: 0,
    onGround: true,
    vitals: new Vitals(breed.maxHp),
    swing: 0,
    struck: 0,
  };
}

/**
 * Roll what one orc leaves behind.
 *
 * Random and unseeded, like every other drop in this game: what falls out of
 * a body is not world state and need not survive a reload.
 */
export function rollOrcDrops(
  breed: OrcBreed, random: () => number = Math.random,
): { item: number; count: number }[] {
  const out: { item: number; count: number }[] = [];
  for (const drop of breed.drops) {
    if (drop.chance !== undefined && random() >= drop.chance) continue;
    const count = drop.min + Math.floor(random() * (drop.max - drop.min + 1));
    if (count > 0) out.push({ item: drop.item, count });
  }
  return out;
}

/** One blow landed. The caller decides what being hit means. */
export interface OrcBlow {
  orc: Orc;
  damage: number;
  /** Whom it landed on. See OrcTarget. */
  at: number;
}

/**
 * One arrow loosed, and which way.
 *
 * A direction rather than an arrow: this file has never heard of an arrow and
 * does not need to. The caller makes one, which is also what lets an orc's
 * arrows and the player's be two separate flights that cannot hit their own
 * side.
 */
export interface OrcShot {
  orc: Orc;
  from: { x: number; y: number; z: number };
  dir: { x: number; y: number; z: number };
  /** Whom it was loosed at. The arrow may still miss. */
  at: number;
}

export interface OrcTurn {
  blows: OrcBlow[];
  shots: OrcShot[];
  /**
   * Balls of cold fire thrown this tick.
   *
   * Their own list rather than more `shots`: an arrow and a ball of fire are
   * different objects with different physics, and the caller has to know
   * which it is being handed before it can make one. Same shape, because
   * what this file knows about either is a place and a direction.
   */
  hurls: OrcShot[];
  /**
   * The ones that died this tick, and where they were standing.
   *
   * Reported rather than simply removed, because what an orc leaves behind is
   * the caller's business - this file knows nothing about gold or about what
   * a pickup is, and should not start now.
   */
  fallen: { breed: OrcBreed; x: number; y: number; z: number }[];
}

/**
 * Where an arrow leaves an orc: from its chest, not its feet.
 *
 * Its position is the bottom of its box, so loosing from there sends every
 * arrow into the ground a block in front of it.
 */
const BOW_HEIGHT = ORC_HEIGHT * 0.8;

/**
 * Advance every orc, and report who got hit.
 *
 * `target` is whoever they are coming for, or null when there is nobody -
 * during which they stand still rather than wandering, because wandering is a
 * behaviour with its own bugs and nothing needs it yet.
 */
/**
 * Somebody an orc might go for.
 *
 * A list rather than one, because a world can have more than one person in
 * it - and an orc that always chased the same one would walk past whoever was
 * standing in front of it. The id is the caller's: a player number over the
 * network, or nought when there is only ever one of you.
 */
export interface OrcTarget {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/**
 * Who an orc may come after: everybody who is actually playing.
 *
 * Somebody in creative is not in the game in the sense that matters here.
 * They cannot be hurt in any way that costs them anything, they are usually
 * flying, and a вовкулака that follows them across the map is a вовкулака
 * that has left the country somebody else is walking through. What it looked
 * like from the inside was being mobbed while building.
 *
 * A function, and in core, because *both* the lone game and the server have
 * to agree about it - the rule living in two places is how one of them ends
 * up not having it.
 */
export function huntable(
  people: readonly {
    readonly id: number; readonly x: number; readonly y: number;
    readonly z: number; readonly flying?: boolean;
  }[],
): OrcTarget[] {
  return people
    .filter((p) => p.flying !== true)
    .map((p) => ({ id: p.id, x: p.x, y: p.y, z: p.z }));
}

/** The one it would go for: the nearest, flat, and only within sight. */
function nearestTo(orc: Orc, targets: readonly OrcTarget[]): OrcTarget | null {
  let best: OrcTarget | null = null;
  let bestFlat = ORC_SIGHT;
  for (const target of targets) {
    const flat = Math.hypot(target.x - orc.x, target.z - orc.z);
    if (flat < bestFlat) { bestFlat = flat; best = target; }
  }
  return best;
}

/**
 * Is there anything solid between these two points?
 *
 * "Щоб тебе моби не могли вдарити через блок." An орк swung whenever
 * somebody was within arm's length and at roughly the same height, which is
 * true of somebody standing on the other side of a wall one block thick -
 * and the wall had no say in it. A player already could not hit *them*
 * through a block; this is the other half of the same rule.
 *
 * Sampled rather than marched. A blow reaches about a block and an arrow a
 * couple of dozen, so a quarter-block step is a handful of tests at worst
 * and cannot skip a cell: the shortest way through a cube is one block, and
 * four samples fit inside that.
 */
export function clearShot(
  isSolid: SolidTest,
  fx: number, fy: number, fz: number,
  tx: number, ty: number, tz: number,
): boolean {
  const dx = tx - fx, dy = ty - fy, dz = tz - fz;
  const span = Math.hypot(dx, dy, dz);
  if (span < 1e-6) return true;
  const steps = Math.ceil(span / 0.25);
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    if (isSolid(
      Math.floor(fx + dx * t),
      Math.floor(fy + dy * t),
      Math.floor(fz + dz * t),
    )) return false;
  }
  return true;
}

export function stepOrcs(
  orcs: Orc[],
  dt: number,
  isSolid: SolidTest,
  targets: readonly OrcTarget[],
  /**
   * Where a raiding orc walks when it can see nobody. Null for "stay put".
   *
   * "Орки будуть йти на другий острів." An орк has always looked twenty-six
   * blocks and no further, which makes a garrison a thing that waits: cross
   * the water, and they are exactly where they were. Given somewhere to be,
   * they walk to it - over the causeway, up the far shore, and into whatever
   * you have built.
   *
   * Only what raids marches. A вовкулака is a wolf in a wood and a скелет is
   * a guard in a room; neither is an army and neither should behave like one.
   */
  march: { readonly x: number; readonly z: number } | null = null,
  /**
   * Break the block there, if it can be broken - for a sapper. The caller
   * owns the world and its rules; true when something came out.
   */
  dig: ((x: number, y: number, z: number) => boolean) | null = null,
): OrcTurn {
  const blows: OrcBlow[] = [];
  const shots: OrcShot[] = [];
  const hurls: OrcShot[] = [];
  const fallen: OrcTurn["fallen"] = [];

  for (let i = orcs.length - 1; i >= 0; i--) {
    const orc = orcs[i]!;
    orc.vitals.step(dt);
    if (orc.vitals.dead) {
      fallen.push({ breed: orc.breed, x: orc.x, y: orc.y, z: orc.z });
      orcs.splice(i, 1);
      continue;
    }
    if (orc.swing > 0) orc.swing = Math.max(0, orc.swing - dt);

    // --- deciding -------------------------------------------------------
    let wantX = 0;
    let wantZ = 0;

    // A friendly one has nobody to come for; it turns to whoever is
    // nearest and stays where it stands.
    const company = orc.breed.friendly === true ? nearestTo(orc, targets) : null;
    const target = orc.breed.friendly === true ? null : nearestTo(orc, targets);
    if (company !== null) {
      const dx = company.x - orc.x;
      const dz = company.z - orc.z;
      if (Math.hypot(dx, dz) > 0.001) orc.yaw = Math.atan2(-dx, -dz);
    }
    const goal = march;
    if (target === null && goal !== null &&
      RAID_FOES.includes(orc.breed.kind)) {
      /*
       * Nobody in sight: march.
       *
       * Straight at it, the way they walk at a person - they step up a block
       * and they drop a few, and the road cut for them does the rest. What
       * they will not do is find their way round a mountain, which is why
       * there is a road.
       */
      const dx = goal.x - orc.x;
      const dz = goal.z - orc.z;
      const flat = Math.hypot(dx, dz);
      if (flat > 1.5) {
        orc.yaw = Math.atan2(-dx, -dz);
        wantX = (dx / flat) * orc.breed.speed;
        wantZ = (dz / flat) * orc.breed.speed;
      }
    }
    if (target !== null) {
      const dx = target.x - orc.x;
      const dz = target.z - orc.z;
      const flat = Math.hypot(dx, dz);

      if (flat > 0.001) {
        // Yaw 0 faces -Z, the same as everything else that has a front.
        orc.yaw = Math.atan2(-dx, -dz);
        const breed = orc.breed;

        if (breed.hurlFrom !== undefined) {
          /*
           * He keeps his distance and throws, and he never closes.
           *
           * The same shape as the archer's rule and for the same reason: a
           * boss that walks into sabre range is a boss you kill by standing
           * still. What is different is that he is *slow*, so backing away
           * from him works and walking round a pillar works - the fight is
           * about the room.
           */
          const tooClose = flat < breed.hurlFrom * 0.6;
          if (tooClose) {
            wantX = -(dx / flat) * breed.speed;
            wantZ = -(dz / flat) * breed.speed;
          } else if (flat > breed.hurlFrom) {
            wantX = (dx / flat) * breed.speed;
            wantZ = (dz / flat) * breed.speed;
          }
          if (flat <= breed.hurlFrom && orc.swing <= 0) {
            orc.swing = breed.swing;
            const from = { x: orc.x, y: orc.y + BOW_HEIGHT, z: orc.z };
            const aimY = target.y + BOW_HEIGHT - from.y;
            const len = Math.hypot(dx, aimY, dz) || 1;
            hurls.push({
              orc, from, at: target.id,
              dir: { x: dx / len, y: aimY / len, z: dz / len },
            });
          }
        } else if (breed.shootFrom !== undefined) {
          /*
           * An archer keeps its distance.
           *
           * It walks in until the target is within bowshot, holds there, and
           * backs away if the target closes - which is what stops it being a
           * sabre orc carrying a bow. The arrow is aimed at the target's
           * chest from the orc's own, so a shot across level ground goes
           * flat rather than into the dirt.
           */
          const tooClose = flat < breed.shootFrom * 0.55;
          if (tooClose) {
            wantX = -(dx / flat) * breed.speed;
            wantZ = -(dz / flat) * breed.speed;
          } else if (flat > breed.shootFrom) {
            wantX = (dx / flat) * breed.speed;
            wantZ = (dz / flat) * breed.speed;
          }
          const canSee = clearShot(
            isSolid, orc.x, orc.y + BOW_HEIGHT, orc.z,
            target.x, target.y + BOW_HEIGHT, target.z,
          );
          if (flat <= breed.shootFrom && orc.swing <= 0 && canSee) {
            orc.swing = breed.swing;
            const from = { x: orc.x, y: orc.y + BOW_HEIGHT, z: orc.z };
            const aimY = target.y + BOW_HEIGHT - from.y;
            const len = Math.hypot(dx, aimY, dz) || 1;
            shots.push({
              orc, from, at: target.id,
              dir: { x: dx / len, y: aimY / len, z: dz / len },
            });
          }
        } else {
          const reachable = flat < ORC_REACH &&
            Math.abs(target.y - orc.y) < ORC_HEIGHT &&
            // And with nothing in the way: an орк on the other side of a
            // wall one block thick is within arm's length of you and always
            // was.
            clearShot(
              isSolid, orc.x, orc.y + ORC_HEIGHT * 0.6, orc.z,
              target.x, target.y + ORC_HEIGHT * 0.6, target.z,
            );
          if (reachable) {
            if (orc.swing <= 0) {
              orc.swing = breed.swing;
              orc.struck++;
              blows.push({ orc, damage: breed.damage, at: target.id });
            }
          } else {
            wantX = (dx / flat) * breed.speed;
            wantZ = (dz / flat) * breed.speed;
          }
        }
      }
    }

    /*
     * And out of each other's way.
     *
     * Every one of them is walking at the same speed towards the same
     * person, so without this they converge and then occupy one cell. The
     * push is strongest at full overlap and falls to nothing at a body's
     * width apart, which makes a band form a ring rather than a column.
     *
     * Only against one at roughly the same height: an орк on a roof is not
     * standing in the one below it, and pushing them apart would drag things
     * sideways off ledges for no reason.
     *
     * Two exactly on top of each other have no direction to be pushed in, so
     * they take one from their ids - stable between frames, and different
     * for each pair, so a stack does not pulse.
     */
    for (const other of orcs) {
      if (other === orc) continue;
      if (Math.abs(other.y - orc.y) >= ORC_HEIGHT) continue;
      let ax = orc.x - other.x;
      let az = orc.z - other.z;
      let gap = Math.hypot(ax, az);
      if (gap >= ORC_WIDTH) continue;
      if (gap < 1e-4) {
        ax = Math.cos(orc.id * 1.7 + other.id);
        az = Math.sin(orc.id * 1.7 + other.id);
        gap = 1;
      }
      const push = ((ORC_WIDTH - gap) / ORC_WIDTH) * ORC_SHOVE;
      wantX += (ax / gap) * push;
      wantZ += (az / gap) * push;
    }

    // --- moving ---------------------------------------------------------
    if (wantX !== 0 || wantZ !== 0) {
      orc.vx = wantX;
      orc.vz = wantZ;
    } else {
      const slow = Math.max(0, 1 - DAMPING * dt);
      orc.vx *= slow;
      orc.vz *= slow;
    }

    orc.vy -= GRAVITY * dt;
    if (orc.vy < -TERMINAL) orc.vy = -TERMINAL;

    const moved = moveAABB(
      orc.x, orc.y, orc.z,
      ORC_WIDTH / 2, ORC_HEIGHT,
      orc.vx * dt, orc.vy * dt, orc.vz * dt,
      isSolid,
      ORC_STEP,
      // Same rule as the player: a step needs a foot on the floor.
      orc.onGround,
    );

    orc.x = moved.x;
    orc.y = moved.y;
    orc.z = moved.z;

    // Walked into something while trying to get somewhere: jump it. One block
    // is all a jump clears, which is what makes a wall two high a wall.
    const blocked = (moved.hitX || moved.hitZ) && (wantX !== 0 || wantZ !== 0);
    if (blocked && moved.onGround) orc.vy = ORC_JUMP;
    else if (moved.onGround || moved.hitCeiling) orc.vy = 0;

    /*
     * A sapper that is still stuck cuts the way: the cell in front at its
     * feet, then the one at its head, a block every `digs` seconds. A step
     * it can jump never gets this far - the jump clears it first.
     */
    const cuts = orc.breed.digs;
    if (cuts !== undefined && dig !== null && blocked) {
      orc.digging = (orc.digging ?? 0) + dt;
      if (orc.digging >= cuts) {
        orc.digging = 0;
        // Every cell the body would stand in a half-step on - the body is
        // wider than the gap between two columns, so the one straight ahead
        // of its middle is not enough: it cut that one and stood there
        // against the next.
        const len = Math.hypot(wantX, wantZ) || 1;
        const px = orc.x + (wantX / len) * 0.5;
        const pz = orc.z + (wantZ / len) * 0.5;
        const half = ORC_WIDTH / 2;
        const feet = Math.floor(orc.y + 0.01);
        cut: for (let y = feet; y <= feet + 1; y++) {
          for (let cx = Math.floor(px - half); cx <= Math.floor(px + half); cx++) {
            for (let cz = Math.floor(pz - half); cz <= Math.floor(pz + half); cz++) {
              if (dig(cx, y, cz)) break cut;
            }
          }
        }
      }
    } else if (!blocked) {
      orc.digging = 0;
    }

    if (moved.hitX) orc.vx = 0;
    if (moved.hitZ) orc.vz = 0;
    orc.onGround = moved.onGround;
  }

  return { blows, shots, hurls, fallen };
}
